import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { motion } from "framer-motion";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import emailjs from "@emailjs/browser";

const MotionCard = motion(Card);
const MotionButton = motion(Button);

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

// EmailJS configuration
const EMAILJS_SERVICE_ID = "service_pjke2ed";
const EMAILJS_TEMPLATE_ID = "template_2390tl7";
const EMAILJS_PUBLIC_KEY = "4b-mOOM__cl9USOdB";

export default function Contact() {
  const [formData, setFormData] = useState({
    studentName: "",
    studentClass: "",
    mobileNumber: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    if (name === "mobileNumber") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // Prevent typing beyond 200 characters
    if (name === "message" && value.length > 200) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    const newErrors = {};

    // Class (Only 6 to 10)
    if (!formData.studentClass.trim()) {
      newErrors.studentClass = "Please enter student class";
    } else {
      const studentClass = parseInt(formData.studentClass.trim(), 10);

      if (isNaN(studentClass) || studentClass < 6 || studentClass > 10) {
        newErrors.studentClass = "Only classes 6 to 10 are allowed";
      }
    }

    // Mobile Number
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Please enter mobile number";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
    }

    // Message
    if (!formData.message.trim()) {
      newErrors.message = "Please enter your message";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);

      const templateParams = {
        student_name: formData.studentName.trim(),
        student_class: formData.studentClass.trim(),
        mobile: formData.mobileNumber.trim(),
        message: formData.message.trim(),
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      setFormData({
        studentName: "",
        studentClass: "",
        mobileNumber: "",
        message: "",
      });

      setErrors({});

      setOpenDialog(true);
    } catch (error) {
      console.log(error);
      alert(error?.text || error?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        py: { xs: 7, md: 10 },
        background: "linear-gradient(180deg,#fff8f2 0%,#ffffff 100%)",
      }}
    >
      <Container maxWidth="lg">
        {/* Heading */}

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          textAlign="center"
          mb={7}
        >
          <Typography
            sx={{
              color: "#FF6B00",
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: "13px",
              mb: 1,
            }}
          >
            CONTACT US
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: "25px",
                sm: "30px",
                md: "36px",
                lg: "42px",
              },
              fontWeight: 800,
              color: "#121212",
              mb: 2,
            }}
          >
            Get In Touch With Us
          </Typography>

          <Typography
            sx={{
              color: "#666",
              fontSize: "15px",
              maxWidth: "650px",
              mx: "auto",
              lineHeight: 1.8,
            }}
          ></Typography>
        </Box>

        {/* Main Section */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1.2fr",
            },
            gap: { xs: 4, md: 5 },
            alignItems: "center",
          }}
        >
          {/* Left Side */}

          <Box
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <Typography
              component={motion.p}
              variants={itemVariants}
              sx={{
                fontSize: { xs: "24px", sm: "26px", lg: "28px" },
                fontWeight: 800,
                color: "#121212",
                mb: 4,
              }}
            >
              Let's Talk
            </Typography>

            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{ display: "flex", gap: 2, mb: 4, alignItems: "flex-start" }}
            >
              <Box
                sx={{
                  width: 55,
                  height: 55,
                  flexShrink: 0,
                  borderRadius: "16px",
                  bgcolor: "#FFF4EB",
                  color: "#FF6B00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocationOnIcon />
              </Box>

              <Box>
                <Typography fontWeight={700}>Address</Typography>
                <Typography color="#666" fontSize={{ xs: "13px", sm: "14px" }}>
                  14-1-211/434, Parvath Nagar, Near HITEC City, Hyderabad,
                  Telangana – 500018
                </Typography>
              </Box>
            </Box>

            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{ display: "flex", gap: 2, mb: 4, alignItems: "flex-start" }}
            >
              <Box
                sx={{
                  width: 55,
                  height: 55,
                  flexShrink: 0,
                  borderRadius: "16px",
                  bgcolor: "#FFF4EB",
                  color: "#FF6B00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CallIcon />
              </Box>

              <Box>
                <Typography fontWeight={700}>Phone</Typography>
                <Typography color="#666" fontSize={{ xs: "13px", sm: "14px" }}>
                  +91-7799559039
                </Typography>
              </Box>
            </Box>

            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
            >
              <Box
                sx={{
                  width: 55,
                  height: 55,
                  flexShrink: 0,
                  borderRadius: "16px",
                  bgcolor: "#FFF4EB",
                  color: "#FF6B00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmailIcon />
              </Box>

              <Box>
                <Typography fontWeight={700}>Email</Typography>
                <Typography color="#666" fontSize={{ xs: "13px", sm: "14px" }}>
                  tutor@vedicwin.com
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Side Form */}

          <MotionCard
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            sx={{
              borderRadius: "24px",
              border: "1px solid #FFE5D0",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
              <Typography
                sx={{
                  fontSize: { xs: "21px", sm: "23px", lg: "24px" },
                  fontWeight: 800,
                  mb: 3,
                  color: "#121212",
                }}
              >
                Send Us a Message
              </Typography>

              <TextField
                fullWidth
                label="Student Name"
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                error={!!errors.studentName}
                helperText={errors.studentName}
                sx={{
                  mb: 2,
                  "& .MuiFormHelperText-root": {
                    ml: 0,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Class"
                name="studentClass"
                value={formData.studentClass}
                onChange={handleChange}
                error={!!errors.studentClass}
                helperText={errors.studentClass}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 2,
                }}
                sx={{
                  mb: 2,
                  "& .MuiFormHelperText-root": {
                    ml: 0,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  mb: 2,
                  "& .MuiFormHelperText-root": {
                    ml: 0,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!errors.message}
                helperText={
                  errors.message ||
                  (formData.message.length > 0
                    ? `${formData.message.length}/200 characters`
                    : "")
                }
                sx={{
                  mb: 2,
                  "& .MuiFormHelperText-root": {
                    ml: 0,
                  },
                }}
              />

              <MotionButton
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleSubmit}
                disabled={loading}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                sx={{
                  display: "flex",
                  mx: "auto",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#FF6B00",
                  py: 1.5,
                  px: 4,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: "#E55D00",
                  },
                }}
              >
                {loading ? "Submitting..." : "Submit Inquiry"}
              </MotionButton>
            </CardContent>
          </MotionCard>
        </Box>
      </Container>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, scale: 0.85, y: 20 },
          animate: { opacity: 1, scale: 1, y: 0 },
          transition: { duration: 0.35, ease: "easeOut" },
          sx: {
            borderRadius: 3,
            p: 1,
            textAlign: "center",
            maxWidth: 420,
            width: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: "#FF6B00",
            fontSize: { xs: "24px", sm: "28px" },
          }}
        >
          🎉 Thank You!
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              color: "#555",
              fontSize: "16px",
              lineHeight: 1.8,
            }}
          >
            Thank you for submitting your inquiry.
            <br />
            We have received your details successfully.
            <br />
            Our team will get back to you as soon as possible.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            sx={{
              bgcolor: "#FF6B00",
              px: 4,
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#E55D00",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}